import { useMutation, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import {
  type ColumnDef,
  getCoreRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import {
  Check,
  CircleDot,
  Copy,
  Key,
  Loader2,
  Mail,
  RotateCcw,
  X,
} from "lucide-react";
import { useMemo, useState } from "react";
import { capitalize } from "shared";
import { toast } from "sonner";
import { useLocalStorage } from "usehooks-ts";
import { createColumnConfigHelper } from "@/components/data-table-filter/core/filters";
import type { FiltersState } from "@/components/data-table-filter/core/types";
import { useDataTableFilters } from "@/components/data-table-filter/hooks/use-data-table-filters";
import {
  createTSTColumns,
  createTSTFilters,
} from "@/components/data-table-filter/integrations/tanstack-table";
import { GivefrontCardIcon } from "@/components/icons/credit-card-icon";
import { RenderStatusBadge } from "@/components/table/cells/RenderStatusBadge";
import { DashedOutCell } from "@/components/table/cells/render-cell";
import { DataTable } from "@/components/table/DataTable";
import { TablePagination } from "@/components/table/DataTablePagination";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { useRequiredRole } from "@/hooks/useRequiredRole";
import { restrictRoute } from "@/lib/routeBlocker";
import type { RouterOutputs } from "@/lib/trpcClient";
import { queryClient, trpc } from "@/lib/trpcClient";
import { env } from "../../../../../../env";

type InviteType = RouterOutputs["org"]["invites"][number];

const dtf = createColumnConfigHelper<InviteType>();

const columnsConfig = [
  dtf
    .option()
    .id("Status")
    .accessor((row) => row.status)
    .displayName("Status")
    .transformOptionFn((u) => ({
      label: capitalize(u),
      value: u,
    }))
    .icon(CircleDot)
    .build(),
  dtf
    .text()
    .id("Email")
    .accessor((row) => row.email)
    .displayName("Email")
    .icon(Mail)
    .build(),
  dtf
    .option()
    .id("Role")
    .accessor((row) => row.role)
    .displayName("Role")
    .transformOptionFn((u) => ({
      label: capitalize(u),
      value: u,
    }))
    .icon(Key)
    .build(),
];

export const Route = createFileRoute(
  "/_authed/_org/organization/people/invitations",
)({
  beforeLoad: async ({ context }) =>
    restrictRoute(context, {
      redirect: {
        to: "/organization/people",
      },
      requiredRole: "viewOnlyAdmin",
    }),
  component: RouteComponent,
  loader: ({ context: { queryClient, trpc } }) => {
    queryClient.ensureQueryData(trpc.org.invites.queryOptions());
  },
});

function RouteComponent() {
  const { admin } = useRequiredRole();
  const [cancelingInviteId, setCancelingInviteId] = useState<string | null>(
    null,
  );
  const [copiedInviteId, setCopiedInviteId] = useState<string | null>(null);

  const {
    data: { user, session },
  } = useSuspenseQuery(trpc.auth.session.queryOptions());
  const [tableFilters, setTableFilters] = useLocalStorage<FiltersState>(
    `@givefront/table-state/v2:${user?.id}-${session?.activeOrganizationId}-invite-filters`,
    [],
  );

  const { data, refetch } = useSuspenseQuery(trpc.org.invites.queryOptions());

  const { mutate: cancelInvite, isPending: isCanceling } = useMutation(
    trpc.org.cancelInvite.mutationOptions({
      onError: (error) => {
        toast.error(error.message);
        setCancelingInviteId(null);
      },
      onSuccess: () => {
        toast.success("Invitation canceled successfully");
        refetch();
        setCancelingInviteId(null);
      },
    }),
  );

  const {
    mutate: resendInvite,
    isPending: isResending,
    variables: resendVariables,
  } = useMutation(
    trpc.org.resendInvite.mutationOptions({
      onError: (error: any) => {
        toast.error(error.message);
      },
      onSuccess: () => {
        toast.success("Invitation resent successfully");
      },
    }),
  );

  const copyInviteLink = async (inviteId: string) => {
    try {
      const inviteUrl = `${env.VITE_CLIENT_BASE_URL}/join/${inviteId}`;
      await navigator.clipboard.writeText(inviteUrl);
      setCopiedInviteId(inviteId);
      toast.success("Invite link copied to clipboard");
      setTimeout(() => setCopiedInviteId(null), 2000);
    } catch (_err) {
      toast.error("Failed to copy invite link");
    }
  };

  const { mutate: quickAccept } = useMutation(
    trpc.dev.quickAcceptInvite.mutationOptions({
      onError: () => {
        toast.error("oops");
      },
      onSuccess: () => {
        refetch();
        toast.success("User quick accepted", {
          description:
            "This is only available in dev (obviously) and their password is 'password'",
        });
      },
    }),
  );

  const tableName = "invitations";

  const tableColumns: ColumnDef<InviteType>[] = [
    {
      accessorFn: (row) => row.email,
      accessorKey: "Email",
      enableGlobalFilter: true,
    },
    {
      accessorFn: (row) => row.role,
      accessorKey: "Role",
      cell: ({ row }) => <RenderStatusBadge status={row.original.role} />,
    },
    {
      accessorFn: (row) => row.cards.length,
      accessorKey: "Scheduled Cards",
      cell: ({ row }) =>
        row.original.cards.length === 0 ? (
          <DashedOutCell />
        ) : (
          <InviteScheduledCardsCell invite={row.original} />
        ),
    },
    {
      accessorFn: (row) => row.status,
      cell: ({ row }) => {
        const invite = row.original;
        return (
          <div className="flex flex-row items-center">
            <RenderStatusBadge status={row.original.status} />
            {admin && invite.status === "pending" && (
              <>
                <Button
                  className="ml-2 size-6 p-0"
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    resendInvite({ inviteId: invite.id });
                  }}
                  disabled={
                    isResending && resendVariables?.inviteId === invite.id
                  }
                  title="Resend invitation"
                >
                  {isResending && resendVariables?.inviteId === invite.id ? (
                    <RotateCcw className="h-4 w-4 animate-spin" />
                  ) : (
                    <Mail className="h-4 w-4" />
                  )}
                </Button>
                <Button
                  className="ml-2 size-6 p-0"
                  size="sm"
                  variant="ghost"
                  onClick={() => copyInviteLink(invite.id)}
                  title="Copy invite link"
                >
                  {copiedInviteId === invite.id ? (
                    <Check className="h-4 w-4 text-green-600" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      className="ml-2 size-6 p-0"
                      size="sm"
                      variant="ghost"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Cancel Invitation</AlertDialogTitle>
                      <AlertDialogDescription>
                        Are you sure you want to cancel the invitation for{" "}
                        {invite.email}? This action cannot be undone.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction
                        disabled={
                          isCanceling && cancelingInviteId === invite.id
                        }
                        onClick={() => {
                          setCancelingInviteId(invite.id);
                          cancelInvite({ inviteId: invite.id });
                        }}
                      >
                        {isCanceling && cancelingInviteId === invite.id
                          ? "Canceling..."
                          : "Cancel Invitation"}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </>
            )}
            {env.VITE_ENVIRONMENT === "development" &&
              invite.status === "pending" && (
                <Button
                  variant={"ghost"}
                  onClick={() =>
                    quickAccept({
                      inviteId: invite.id,
                    })
                  }
                >
                  <Check />
                </Button>
              )}
          </div>
        );
      },

      id: "Status",
    },
  ];

  const { columns, filters, actions } = useDataTableFilters({
    columnsConfig,
    data,
    filters: tableFilters,
    onFiltersChange: setTableFilters,
    strategy: "client",
  });

  const tstColumns = useMemo(
    () =>
      createTSTColumns({
        columns: tableColumns,
        configs: columns,
      }),
    [columns],
  );
  const tstFilters = useMemo(() => createTSTFilters(filters), [filters]);

  const table = useReactTable({
    columnResizeMode: "onChange",
    columns: tstColumns,
    data: data,
    getCoreRowModel: getCoreRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getRowId: (row) => row.id.toString(),
    getSortedRowModel: getSortedRowModel(),
    initialState: {
      pagination: {
        pageIndex: 0,
        pageSize: 25,
      },
    },
    state: {
      columnFilters: tstFilters,
      columnPinning: {
        right: ["Status"],
      },
    },
  });

  return (
    <div className="flex h-screen flex-col">
      <DataTable
        noResultsComponent={
          <div className="flex h-36 w-full flex-col items-center justify-center space-y-4">
            <span className="text-xl">No Matching Invites</span>

            <Button onClick={actions?.removeAllFilters} variant={"outline"}>
              Clear Filters
            </Button>
          </div>
        }
        table={table}
        tableName={tableName}
        className="[&_th]:border-t-0!"
      />

      <TablePagination tableName={tableName} table={table} />
    </div>
  );
}

function InviteScheduledCardsCell({ invite }: { invite: InviteType }) {
  const {
    mutate: deleteScheduledCard,
    isPending: isDeletingScheduledCard,
    variables,
  } = useMutation(
    trpc.card.cancelScheduledCard.mutationOptions({
      onSuccess: () =>
        queryClient.invalidateQueries(trpc.org.invites.queryOptions()),
    }),
  );

  return (
    <div className="flex flex-wrap gap-2">
      {invite.cards.map((c) => (
        <div className="flex w-fit items-center gap-2 rounded-sm border border-border bg-neutral-100 px-2 py-0.5 font-medium text-neutral-700 text-xs">
          <GivefrontCardIcon variant={c.type} className="size-6" /> {c.name}{" "}
          <button
            type="button"
            onClick={() => deleteScheduledCard({ id: c.id })}
          >
            {isDeletingScheduledCard && variables?.id === c.id ? (
              <Loader2 className="size-3 animate-spin" />
            ) : (
              <X className="size-3" />
            )}
          </button>
        </div>
      ))}
    </div>
  );
}
